import type { SessionSnapshot } from '@scrambl/shared/session-scope'
import { SessionChangedError } from '@scrambl/shared/session-scope'

import { ApiError } from '../api/api-error'

type SelectionOutcome =
  | 'verified'
  | 'expired'
  | 'unavailable'
  | 'missing-account'

/** Correlate a saved selection with its native epoch; this is not account authorization. */
export async function verifySavedDesktopAccount(
  accountId: string,
  ports: {
    assertSelectionCurrent(): void
    captureSession(): Promise<SessionSnapshot>
    assertSessionCurrent(session: SessionSnapshot): Promise<void>
    select(
      id: string,
      expected: SessionSnapshot,
    ): Promise<SessionSnapshot | null>
    profile(): Promise<{ success: boolean; data?: { id: string } | null }>
    refresh(): Promise<boolean>
    isRefreshDefinitive(): boolean
    accept(): void
  },
): Promise<SelectionOutcome> {
  let selected: SessionSnapshot | null = null
  let originalSession: SessionSnapshot | null = null
  let previousId: string | null = null
  let outcome: SelectionOutcome = 'unavailable'
  const assertOwned = async () => {
    ports.assertSelectionCurrent()
    const expected = selected ?? originalSession
    if (expected) await ports.assertSessionCurrent(expected)
    ports.assertSelectionCurrent()
  }
  const selectOwned = (id: string, expected: SessionSnapshot) =>
    ports.select(id, {
      ...expected,
      assertCurrent() {
        ports.assertSelectionCurrent()
        expected.assertCurrent()
      },
    })
  try {
    const original = await ports.captureSession()
    originalSession = original
    ports.assertSelectionCurrent()
    original.assertCurrent()
    previousId = original.identity
    selected = await selectOwned(accountId, original)
    ports.assertSelectionCurrent()
    if (!selected) {
      await assertOwned()
      return 'missing-account'
    }
    if (selected.identity !== accountId) throw new SessionChangedError()
    await assertOwned()

    const readProfile = async () => {
      const response = await ports.profile()
      await assertOwned()
      if (!response.success || !response.data) return false
      if (response.data.id !== accountId)
        throw new Error('The selected account could not be verified.')
      return true
    }
    let verified = false
    try {
      verified = await readProfile()
    } catch (error) {
      await assertOwned()
      if (!(error instanceof ApiError) || error.status !== 401) throw error
    }
    if (!verified) {
      const refreshed = await ports.refresh()
      await assertOwned()
      if (refreshed) verified = await readProfile()
      else outcome = ports.isRefreshDefinitive() ? 'expired' : 'unavailable'
    }
    if (verified) {
      await assertOwned()
      ports.accept()
      return 'verified'
    }
  } catch (error) {
    // A foreign login/selection is not an expired saved account and must never
    // trigger a rollback. Transport failures keep a bounded public outcome.
    if (error instanceof SessionChangedError) throw error
    await assertOwned()
  }

  // One owned rollback attempt, outside the catch above. A lost reply is not
  // permission to replay a selection that may already have committed.
  if (selected && previousId && previousId !== accountId) {
    await assertOwned()
    const restored = await selectOwned(previousId, selected)
    ports.assertSelectionCurrent()
    if (!restored) return 'unavailable'
    await ports.assertSessionCurrent(restored)
    ports.assertSelectionCurrent()
  }
  return outcome
}
